import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
  comm100Config, isLiveChatSuppressed, openComm100, subscribeLiveChatSuppression,
} from './liveChatConfig';

/* Comm100 canlı destek (22.10.2026; sunucu: services/comm100.py).
 *
 * Canlı Betrun'un gömme kodunun aynısını kurar: `Comm100API` genel nesnesi,
 * düğme kutusu (`#comm100-button-<plan>`) ve betik — birinci sunucu, 5 sn içinde
 * yüklenmezse sıradaki. Özel koddan (geçişte taşınan body kodu) zaten
 * yüklendiyse hiçbir şey eklenmez; menüdeki düğme yine `openComm100` ile açar.
 *
 * Ziyaretçi bilgisi (ad, e-posta, oyuncu kodu) yalnız panelde "Ziyaretçi
 * bilgisini gönder" açıksa geçer: kişisel veri üçüncü tarafa gider.
 * Kabuk Comm100'ün yüzen düğmesini gizlemek isterse (`hide_button`) ya da
 * tam ekran sağlayıcı çerçevesi açıkken (bastırma) gövdeye sınıf yazılır;
 * düğme App.css'te gizlenir. */

const FALLBACK_MS = 5000;
const BODY_HIDE = 'comm100-hide';

const setVisitor = (api, user) => {
  if (!user || typeof api?.set !== 'function') return;
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.username || '';
  try {
    if (name) api.set('livechat.visitor.name', name);
    if (user.email) api.set('livechat.visitor.email', user.email);
    if (user.player_code) api.set('livechat.visitor.customVariables', [{ name: 'Oyuncu Kodu', value: String(user.player_code) }]);
  } catch {
    // Comm100 sürümüne göre alan adları değişebilir; destek yine açılır.
  }
};

export const installComm100 = (config, { doc = document, win = window } = {}) => {
  if (!config) return false;
  const existing = win.Comm100API;
  if (existing && existing.site_id) return false; // özel koddan yüklendi
  const api = existing || {};
  win.Comm100API = api;
  api.chat_buttons = api.chat_buttons || [];
  const divId = `comm100-button-${config.code_plan}`;
  if (!doc.getElementById(divId)) {
    const box = doc.createElement('div');
    box.id = divId;
    doc.body.appendChild(box);
  }
  api.chat_buttons.push({ code_plan: config.code_plan, div_id: divId });
  api.site_id = Number(config.site_id);
  api.main_code_plan = config.code_plan;
  const load = (host) => {
    const script = doc.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://${host}/livechat.ashx?siteId=${api.site_id}`;
    script.setAttribute('data-bhs-comm100', host);
    doc.body.appendChild(script);
  };
  const [first, ...rest] = config.hosts;
  load(first);
  rest.forEach((host, index) => {
    win.setTimeout(() => { if (!api.loaded) load(host); }, FALLBACK_MS * (index + 1));
  });
  return true;
};

/* Sitenin her zaman görünen düğmesi Comm100'ün düğme kutusudur (`#comm100-button-…`;
 * canlıda özel CSS'le kulaklık simgeli kutu). Comm100 pencereyi bir kez açıp
 * küçültünce kutudaki kendi çerçevesini gizler ve ekranın köşesine ikinci bir düğme
 * ekler (App.css'te gizlenir). Kutu görünür kalır ama içi boştur: tıklama artık
 * hiçbir şey açmıyordu. Kutunun çerçevesi görünmüyorsa tıklama sohbeti Comm100'ün
 * kendi komutuyla açar (25.10.2026). Çerçeve görünürken tıklama zaten içine gider. */
export const BUTTON_SELECTOR = '[id^="comm100-button-"]';

export const buttonFrameHidden = (box, win = window) => {
  const frame = box?.querySelector('iframe');
  if (!frame) return true;
  return win.getComputedStyle(frame).display === 'none';
};

export const handleButtonClick = (event, { win = window, open = openComm100 } = {}) => {
  const box = event.target?.closest?.(BUTTON_SELECTOR);
  if (!box || !buttonFrameHidden(box, win)) return false;
  event.preventDefault();
  return open(box.id.slice('comm100-button-'.length));
};

/* Comm100 sohbet penceresi açık mı: gövdeye `comm100-window-open` yazılır, böylece
 * Comm100'ün köşe düğmesi yalnız pencere açıkken (küçültme düğmesi olarak) görünür. */
export const WINDOW_OPEN_CLASS = 'comm100-window-open';
const WINDOW_ID = 'chat_window_container';

export const syncWindowClass = (doc = document) => {
  const frame = doc.getElementById(WINDOW_ID);
  const open = Boolean(frame) && doc.defaultView.getComputedStyle(frame).display !== 'none';
  doc.body.classList.toggle(WINDOW_OPEN_CLASS, open);
  return open;
};

const Comm100Support = () => {
  const settings = useSettings();
  const { user } = useAuth();
  const config = comm100Config(settings, user);
  const plan = config?.code_plan || '';

  useEffect(() => {
    if (!config) return;
    installComm100(config);
  }, [plan]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!config?.send_visitor || !user) return undefined;
    const api = window.Comm100API;
    if (!api) return undefined;
    // Betik yüklenince çalışır; zaten yüklüyse hemen.
    const previous = api.onReady;
    api.onReady = () => {
      if (typeof previous === 'function') previous();
      setVisitor(api, user);
    };
    if (api.loaded) setVisitor(api, user);
    return undefined;
  }, [plan, config?.send_visitor, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Gömme kod panelin özel kodundan da gelebilir (canlı BETRUN): dinleyici ayardan bağımsızdır.
    const onClick = (event) => { handleButtonClick(event); };
    document.addEventListener('click', onClick, true);
    // Pencerenin görünürlüğü Comm100'ün kendi stil değişikliğidir: gövdeye eklenen
    // kapsayıcıyı bekle (yalnız doğrudan çocuklar), sonra yalnız pencere çerçevesini izle.
    let watched = null;
    const frameObserver = new MutationObserver(() => syncWindowClass());
    const attach = () => {
      const frame = document.getElementById(WINDOW_ID);
      if (!frame || frame === watched) return;
      watched = frame;
      frameObserver.disconnect();
      frameObserver.observe(frame, { attributes: true, attributeFilter: ['style', 'class'] });
      syncWindowClass();
    };
    const bodyObserver = new MutationObserver(attach);
    bodyObserver.observe(document.body, { childList: true });
    attach();
    return () => {
      document.removeEventListener('click', onClick, true);
      bodyObserver.disconnect();
      frameObserver.disconnect();
      document.body.classList.remove(WINDOW_OPEN_CLASS);
    };
  }, []);

  useEffect(() => {
    const apply = (suppressed) => {
      document.body.classList.toggle(BODY_HIDE, Boolean(config && (config.hide_button || suppressed)));
    };
    apply(isLiveChatSuppressed());
    const release = subscribeLiveChatSuppression(apply);
    return () => {
      release();
      document.body.classList.remove(BODY_HIDE);
    };
  }, [plan, config?.hide_button]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default Comm100Support;
